/**
 * useUltrasoundForm - Custom React Hook
 * Centralizes form logic for ultrasound examination worksheets
 *
 * Provides:
 * - Form data state management
 * - Form submission handling
 * - PDF generation and download
 * - Loading and error state management
 * - Field-level validation with inline errors
 * - Form draft auto-save / restore via localStorage
 * - Form reset functionality
 */

import { useState, useCallback, useReducer, useEffect, useRef } from 'react';
import {
  generateReportFiles, saveReportFiles, describeSavedReport,
  reservePreviewWindow, abandonPreviewWindow, showReportPreview, postReportAudit
} from '../generators/reportOutput';

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const DRAFT_MAX_SIZE = 50000; // chars — skip save if form data is too large

/**
 * Action types for form state reducer
 */
const ACTIONS = {
  SET_FIELD: 'SET_FIELD',
  SET_MULTIPLE_FIELDS: 'SET_MULTIPLE_FIELDS',
  RESET_FORM: 'RESET_FORM',
  MERGE_FORM_DATA: 'MERGE_FORM_DATA'
};

/**
 * Form reducer function for complex state updates
 */
const formReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_FIELD:
      return {
        ...state,
        [action.payload.field]: action.payload.value
      };

    case ACTIONS.SET_MULTIPLE_FIELDS:
      return {
        ...state,
        ...action.payload
      };

    case ACTIONS.MERGE_FORM_DATA:
      return {
        ...state,
        ...action.payload
      };

    case ACTIONS.RESET_FORM:
      return action.payload;

    default:
      return state;
  }
};

const REQUIRED_FIELD_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  mrn: 'MRN',
  examDate: 'Exam date'
};

/**
 * useUltrasoundForm - Main custom hook
 *
 * @param {Object} initialFormData - Initial form data structure
 * @param {string} examType - Type of exam (e.g., 'Renal', 'DVT')
 * @param {Object} options - Configuration options
 * @returns {Object} Hook state and methods
 */
export const useUltrasoundForm = (
  initialFormData,
  examType,
  options = {}
) => {
  // Configuration
  const {
    apiUrl = process.env.REACT_APP_API_URL,
    token = localStorage.getItem('authToken'),
    facilityInfo = null,
    onSuccess = null,
    onError = null,
    validateBeforeSubmit = null
  } = options;

  // Form state using reducer for complex updates
  const [formData, dispatch] = useReducer(formReducer, initialFormData);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Field-level validation errors: { firstName: 'First name is required', ... }
  const [fieldErrors, setFieldErrors] = useState({});

  // Draft restore banner state — set on mount if a valid draft exists
  const [draftToRestore, setDraftToRestore] = useState(null);

  // Auto-save debounce timer ref
  const autoSaveTimerRef = useRef(null);

  // --- Draft helpers ---

  const draftKey = examType ? `us_draft_${examType}` : null;

  /**
   * Returns the saved draft object { savedAt, data } or null.
   * Auto-discards drafts older than 24 hours.
   */
  const loadDraft = useCallback(() => {
    if (!draftKey) return null;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt || !parsed.data) return null;
      if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
        localStorage.removeItem(draftKey);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, [draftKey]);

  /**
   * Removes the draft from localStorage.
   */
  const clearDraft = useCallback(() => {
    if (draftKey) {
      localStorage.removeItem(draftKey);
    }
  }, [draftKey]);

  // On mount: check localStorage for a valid draft and surface it for the banner
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const draft = loadDraft();
    if (draft) setDraftToRestore(draft);
  }, []); // intentionally run only on mount

  // Auto-save: debounce writes to localStorage whenever formData changes
  useEffect(() => {
    if (!draftKey) return;

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      try {
        const serialized = JSON.stringify(formData);
        if (serialized.length > DRAFT_MAX_SIZE) return; // skip oversized saves
        const draft = { savedAt: Date.now(), data: formData };
        localStorage.setItem(draftKey, JSON.stringify(draft));
      } catch {
        // localStorage quota or serialization failure — silently skip
      }
    }, 500);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, draftKey]);

  // --- Field operations ---

  /**
   * Set a single form field. Clears that field's validation error.
   */
  const updateField = useCallback((field, value) => {
    dispatch({ type: ACTIONS.SET_FIELD, payload: { field, value } });
    // Clear validation error for this field when the user edits it
    if (REQUIRED_FIELD_LABELS[field] || field === 'supervisingSonographer') {
      setFieldErrors(prev => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, []);

  /**
   * Set multiple form fields at once.
   */
  const updateMultipleFields = useCallback((fields) => {
    dispatch({ type: ACTIONS.SET_MULTIPLE_FIELDS, payload: fields });
  }, []);

  /**
   * Update nested object fields (e.g., 'rightKidney.size1' or 'section', 'field', value)
   */
  const updateNestedField = useCallback((section, fieldOrValue, optionalValue) => {
    let sectionName, fieldName, value;

    if (typeof fieldOrValue === 'string' && optionalValue !== undefined) {
      sectionName = section;
      fieldName = fieldOrValue;
      value = optionalValue;
    } else {
      const parts = section.split('.');
      if (parts.length !== 2) {
        console.error('Invalid nested field path:', section);
        return;
      }
      sectionName = parts[0];
      fieldName = parts[1];
      value = fieldOrValue;
    }

    const updatedSection = {
      ...formData[sectionName],
      [fieldName]: value
    };

    updateField(sectionName, updatedSection);
  }, [formData, updateField]);

  /**
   * Clear a specific field's validation error programmatically.
   */
  const clearFieldError = useCallback((fieldName) => {
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  // --- Messages ---

  /**
   * Clear error and success messages.
   */
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  // --- Form operations ---

  /**
   * Reset form to initial state and clear draft.
   */
  const resetForm = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_FORM, payload: initialFormData });
    setFieldErrors({});
    clearMessages();
    clearDraft();
  }, [initialFormData, clearMessages, clearDraft]);

  /**
   * Validate form data. Returns true if valid.
   * Populates fieldErrors state for inline display.
   */
  const validateForm = useCallback(() => {
    const errors = {};

    for (const [field, label] of Object.entries(REQUIRED_FIELD_LABELS)) {
      const val = formData[field];
      if (!val || (typeof val === 'string' && !val.trim())) {
        errors[field] = `${label} is required`;
      }
    }

    // Student worksheets (tagged by PatientInfo) need a supervising sonographer
    if (formData.studentWorksheet && !(formData.supervisingSonographer || '').trim()) {
      errors.supervisingSonographer = 'Supervising sonographer is required';
    }

    // Custom validation if provided
    if (validateBeforeSubmit) {
      const customError = validateBeforeSubmit(formData);
      if (customError) {
        setError(customError);
        setFieldErrors(errors);
        return false;
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fill in all required fields.');
      return false;
    }

    setFieldErrors({});
    return true;
  }, [formData, validateBeforeSubmit]);

  /**
   * Handle form submission — validates, generates PDF client-side, downloads it.
   * No PHI is sent to the server.
   */
  const handleSubmit = useCallback(async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    clearMessages();
    setIsLoading(true);

    try {
      if (!validateForm()) {
        setIsLoading(false);
        return false;
      }

      if (!examType) throw new Error('Exam type not specified');

      console.log(`Generating ${examType} report client-side...`);

      // PDF, or one PNG per page, depending on the facility's report format
      const files = await generateReportFiles(examType, formData, facilityInfo);
      await saveReportFiles(files);
      const fileName = files.map((f) => f.fileName).join(', ');

      const successMsg = describeSavedReport(files);
      setSuccess(successMsg);
      console.log(successMsg);

      // Post audit ping — only examType and format, no PHI
      postReportAudit({ apiUrl, token, examType, facilityInfo });

      clearDraft();
      if (onSuccess) onSuccess({ fileName, fileNames: files.map((f) => f.fileName) });
      return true;
    } catch (err) {
      const errorMsg = err.message || 'Error generating report';
      setError(errorMsg);
      console.error('Form submission error:', err);
      if (onError) onError(err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [formData, examType, apiUrl, token, facilityInfo, validateForm, clearMessages, clearDraft, onSuccess, onError]);

  /**
   * Handle PDF preview — validates, generates PDF client-side, opens in a new tab.
   * No PHI is sent to the server.
   */
  const handlePreview = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    clearMessages();
    setIsLoading(true);
    let previewWindow = null;

    try {
      if (!validateForm()) {
        setIsLoading(false);
        return false;
      }

      if (!examType) throw new Error('Exam type not specified');

      // PNG facilities: reserve the tab now, inside the click, before slow rasterising
      previewWindow = reservePreviewWindow(facilityInfo);
      const files = await generateReportFiles(examType, formData, facilityInfo);
      await showReportPreview(files, previewWindow);

      return true;
    } catch (err) {
      abandonPreviewWindow(previewWindow);
      setError(err.message || 'Error generating preview');
      console.error('Preview error:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [formData, examType, facilityInfo, validateForm, clearMessages]);

  /**
   * Check if form has required patient info (boolean convenience).
   */
  const hasRequiredPatientInfo = useCallback(() => {
    return !!(formData.firstName && formData.lastName && formData.mrn && formData.examDate);
  }, [formData]);

  /**
   * Get a deep clone of current formData (for debugging).
   */
  const getFormDataSnapshot = useCallback(() => {
    return JSON.parse(JSON.stringify(formData));
  }, [formData]);

  /**
   * Merge partial form data (used by draft restore).
   */
  const mergeFormData = useCallback((partialData) => {
    dispatch({ type: ACTIONS.MERGE_FORM_DATA, payload: partialData });
  }, []);

  return {
    // State
    formData,
    isLoading,
    error,
    success,
    fieldErrors,

    // Single field operations
    updateField,
    updateMultipleFields,
    updateNestedField,

    // Validation
    clearFieldError,
    validateForm,
    hasRequiredPatientInfo,
    isFormValid: hasRequiredPatientInfo(),

    // Form operations
    resetForm,
    handleSubmit,
    handlePreview,
    mergeFormData,

    // Draft operations
    loadDraft,
    clearDraft,

    // Draft banner props — spread into <DraftRestoreBanner> or check for null
    draftBannerProps: draftToRestore
      ? {
          savedAt: draftToRestore.savedAt,
          onRestore: () => {
            mergeFormData(draftToRestore.data);
            setDraftToRestore(null);
          },
          onDiscard: () => {
            clearDraft();
            setDraftToRestore(null);
          }
        }
      : null,

    // Utility
    clearMessages,
    getFormDataSnapshot,

    // Info
    examType
  };
};

export default useUltrasoundForm;
